import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { useChat, useNavigation, useSidebar } from "../../hooks";
import { useAuth } from "../../hooks/useAuth";
import { ChatService } from "../../services/chatService";
import { HealthThreatData } from "../../types/health-threat";
import { MedicalSummaryData } from "../../types/medical-summary";
import { Button } from "../catalyst/button";
import { Sidebar } from "../layout";
import { MhwMaintenanceBanner } from "../common";
import HealthThreatModal from "../modals/HealthThreatModal";
import MedicalSummaryModal from "../modals/MedicalSummaryModal";
import Icon from "../ui/Icon";
import { MessageBubble } from "./chat";

const HomeScreen: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const { navigateToAuth } = useNavigation();
  const {
    currentChat,
    message,
    setMessage,
    isLoading,
    loadChatList,
    loadChat,
    startNewChat,
    sendMessage,
    createNewChat,
    anonymousMessages,
    sendAnonymousMessage,
    isStreaming,
    streamingContent,
    stopStreaming,
  } = useChat();
  const { isLoggedIn } = useAuth();
  const {
    isOpen: isSidebarOpen,
    toggle: toggleSidebar,
    close: closeSidebar,
  } = useSidebar();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const currentChatIdRef = useRef(currentChat?.id);
  currentChatIdRef.current = currentChat?.id;

  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [summaryData, setSummaryData] = useState<MedicalSummaryData | null>(
    null
  );
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [isHealthThreatModalOpen, setIsHealthThreatModalOpen] = useState(false);
  const [healthThreatData, setHealthThreatData] =
    useState<HealthThreatData | null>(null);
  const [isHealthThreatLoading, setIsHealthThreatLoading] = useState(false);
  const [healthThreatError, setHealthThreatError] = useState<string | null>(
    null
  );

  const handleOpenMedicalSummary = useCallback(async () => {
    setIsSummaryModalOpen(true);
    setIsSummaryLoading(true);
    setSummaryError(null);
    setSummaryData(null);

    try {
      const data = await ChatService.getMedicalSummary(
        currentChat?.id || undefined
      );
      setSummaryData(data);
    } catch (error) {
      console.error("문진요약서 생성 실패:", error);
      setSummaryError("문진요약서를 생성하는데 실패했습니다.");
      toast.error("문진요약서를 생성하는데 실패했습니다.");
    } finally {
      setIsSummaryLoading(false);
    }
  }, [currentChat?.id]);

  const handleOpenHealthThreat = useCallback(async () => {
    setIsHealthThreatModalOpen(true);
    setIsHealthThreatLoading(true);
    setHealthThreatError(null);
    setHealthThreatData(null);

    try {
      const data = await ChatService.getHealthThreat();
      setHealthThreatData(data);
    } catch (error) {
      console.error("건강 위험 분석 실패:", error);
      setHealthThreatError("건강 위험도를 분석하는데 실패했습니다.");
      toast.error("건강 위험도를 분석하는데 실패했습니다.");
    } finally {
      setIsHealthThreatLoading(false);
    }
  }, []);

  // URL 파라미터의 chatId로 채팅 로드 (새로고침, 사이드바 선택 시)
  useEffect(() => {
    if (chatId && isLoggedIn && chatId !== currentChatIdRef.current) {
      loadChat(chatId);
    }
  }, [chatId, isLoggedIn, loadChat]);

  // 홈(/)으로 이동 시 채팅 상태 초기화
  useEffect(() => {
    if (!chatId) {
      createNewChat();
    }
  }, [chatId, createNewChat]);

  // 새 채팅 생성 후 실제 ID를 받으면 URL에 반영
  // temp/partial → real ID 전환 시에만 동작 (채팅 간 전환 시에는 무시)
  const prevChatIdForSyncRef = useRef<string | undefined>(currentChat?.id);
  useEffect(() => {
    const prevId = prevChatIdForSyncRef.current;
    prevChatIdForSyncRef.current = currentChat?.id;

    if (
      currentChat?.id &&
      !currentChat.id.startsWith("temp-") &&
      !currentChat.id.startsWith("partial-") &&
      (!prevId || prevId.startsWith("temp-") || prevId.startsWith("partial-"))
    ) {
      const targetPath = `/chat/${currentChat.id}`;
      if (window.location.pathname !== targetPath) {
        navigate(targetPath, { replace: true });
      }
    }
  }, [currentChat?.id, navigate]);

  // 로그인 상태에서 채팅 목록 로드
  useEffect(() => {
    if (isLoggedIn) {
      loadChatList();
    }
  }, [isLoggedIn, loadChatList]);

  // 메시지가 추가되거나 스트리밍 콘텐츠가 변경될 때 스크롤을 하단으로
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentChat?.messages, anonymousMessages, streamingContent]);

  const handleSendMessage = async () => {
    const content = message.trim();
    if (!content) return;

    // 입력 필드 즉시 초기화 (스트리밍 전에)
    setMessage("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    if (isLoggedIn) {
      if (currentChat) {
        await sendMessage(currentChat.id, content);
      } else {
        try {
          await startNewChat(content);
        } catch (error) {
          console.error("새 채팅 시작 실패:", error);
        }
      }
    } else {
      try {
        await sendAnonymousMessage(content);
      } catch (error) {
        console.error("익명 메시지 전송 실패:", error);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // 텍스트 에어리어 자동 높이 조절
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    setMessage(textarea.value);

    // 높이 자동 조절 (최대 120px)
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  // 메시지 전송 후 스크롤 조정
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        block: "end",
        behavior: "smooth",
      });
    }, 0);
  };

  return (
    <div className="h-full bg-white flex flex-col relative overflow-hidden">
      {/* 사이드바 */}
      <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} />

      {/* 상단 헤더 - 고정 */}
      <header className="sticky top-0 z-10 bg-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          {/* 햄버거 메뉴 버튼 - 항상 표시 */}
          <button
            onClick={toggleSidebar}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Icon name="menu" className="text-text-secondary" />
          </button>
        </div>

        <div className="flex items-center space-x-2">
          {/* 로그인/회원가입 버튼 - 비로그인 사용자에게만 표시 */}
          {!isLoggedIn && (
            <Button
              onClick={navigateToAuth}
              outline
              className="text-s"
            >
              로그인/회원가입
            </Button>
          )}
        </div>
      </header>

      {/* 의정원 점검 안내 (점검 시간창 밖에서는 비표시) */}
      <MhwMaintenanceBanner />

      {/* 메인 콘텐츠 - 스크롤 영역 */}
      <main className="flex-1 flex flex-col overflow-y-auto overscroll-contain touch-scroll">
        {(isLoggedIn && (currentChat || isStreaming)) ||
        (!isLoggedIn && (anonymousMessages.length > 0 || isStreaming)) ? (
          // 채팅 메시지 영역
          <>
            <div className="flex-1 px-4 py-4 space-y-4">
              {/* 로그인한 사용자의 채팅 메시지 */}
              {isLoggedIn &&
                currentChat?.messages.map((msg) => (
                  <MessageBubble key={msg.id} message={msg} />
                ))}
              {/* 익명 사용자의 채팅 메시지 */}
              {!isLoggedIn &&
                anonymousMessages.map((msg) => (
                  <MessageBubble
                    key={msg.id}
                    message={{
                      id: msg.id,
                      chatId: "",
                      role: msg.role,
                      content: msg.content,
                      createdAt: msg.timestamp,
                    }}
                  />
                ))}
              {/* 스트리밍 중인 AI 응답 표시 */}
              {isStreaming && (
                <MessageBubble
                  message={{
                    id: "streaming",
                    chatId: currentChat?.id || "",
                    role: "AI",
                    content: streamingContent || "",
                    createdAt: new Date().toISOString(),
                  }}
                  isStreaming
                />
              )}
              <div ref={messagesEndRef} />
            </div>
            {/* HS-03: 문진요약서 생성 플로팅 버튼 */}
            {isLoggedIn && currentChat && !isStreaming && !isSummaryModalOpen && (
              <div className="sticky bottom-2 z-10 flex justify-center pointer-events-none -mt-10">
                <button
                  onClick={handleOpenMedicalSummary}
                  className="pointer-events-auto flex items-center space-x-2 bg-white border border-blue-200 rounded-full px-4 py-2 shadow-md hover:bg-blue-50 transition-colors"
                >
                  <Icon name="clipboard" size="sm" className="text-blue-600" />
                  <span className="text-xs font-medium text-blue-700">
                    문진요약서 생성
                  </span>
                </button>
              </div>
            )}
          </>
        ) : (
          // 초기 화면
          <div className="flex-1 flex flex-col items-center justify-center px-6">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-semibold text-text-primary">
                내 의료기록을 연동하고,
                <br />
                안전한 건강/의료 질문 시작하기
              </h1>
            </div>

            {/* HS-01, HS-02: 문진요약서 영역 - 로그인 사용자에게만 */}
            {isLoggedIn && (
              <div className="w-full max-w-sm mb-4">
                <button
                  onClick={handleOpenMedicalSummary}
                  className="w-full bg-white border border-border-default rounded-xl p-4 hover:bg-gray-50 transition-colors text-left shadow-sm"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon
                        name="clipboard"
                        size="md"
                        className="text-blue-600"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-text-primary">
                        문진요약서
                      </p>
                      <p className="text-xs text-text-secondary mt-0.5">
                        내 건강 데이터 기반 문진 요약서를 확인하세요
                      </p>
                    </div>
                    <Icon
                      name="chevronRight"
                      size="sm"
                      className="text-text-tertiary ml-auto flex-shrink-0"
                    />
                  </div>
                </button>
              </div>
            )}

            {/* HR-01~03: 내 건강 위험 & 관리 - 로그인 사용자에게만 */}
            {isLoggedIn && (
              <div className="w-full max-w-sm mb-4">
                <button
                  onClick={handleOpenHealthThreat}
                  className="w-full bg-white border border-border-default rounded-xl p-4 hover:bg-gray-50 transition-colors text-left shadow-sm"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon
                        name="shield"
                        size="md"
                        className="text-orange-600"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-text-primary">
                        내 건강 위험 & 관리
                      </p>
                      <p className="text-xs text-text-secondary mt-0.5">
                        건강검진·투약·검사 기반 위험도를 확인하세요
                      </p>
                    </div>
                    <Icon
                      name="chevronRight"
                      size="sm"
                      className="text-text-tertiary ml-auto flex-shrink-0"
                    />
                  </div>
                </button>
              </div>
            )}

            {/* 연동하기 버튼 - 로그인하지 않은 사용자에게만 표시 */}
            {!isLoggedIn && (
              <div className="w-full max-w-sm mb-6">
                <Button
                  onClick={navigateToAuth}
                  color="zinc"
                  className="w-full flex items-center justify-center"
                >
                  <Icon name="document" />
                  <span className="font-medium">내 의료기록 연동하기</span>
                </Button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* 메시지 입력 영역 - 하단 고정 */}
      <div className="sticky bottom-0 z-10 bg-white p-4">
        <div className="bg-gray-100 rounded-3xl p-2 flex items-end space-x-2">
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleTextareaChange}
              onKeyPress={handleKeyPress}
              onFocus={scrollToBottom}
              placeholder="건강/의료 관련 질문을 시작해보세요"
              className="w-full resize-none bg-transparent px-3 py-2 text-s text-text-primary placeholder-text-tertiary focus:outline-none"
              style={{
                minHeight: "36px",
                maxHeight: "120px",
                fontSize: "16px", // iOS 자동 줌 방지
              }}
              disabled={isLoading || isStreaming}
            />
          </div>
          {isStreaming ? (
            /* 스트리밍 중: 중단 버튼 (■ 사각형 아이콘) */
            <button
              onClick={stopStreaming}
              className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-800 hover:bg-black text-white transition-all duration-200"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                <rect width="12" height="12" rx="2" />
              </svg>
            </button>
          ) : (
            /* 평상시: 전송 버튼 */
            <button
              onClick={handleSendMessage}
              disabled={!message.trim() || isLoading}
              className={clsx(
                "flex items-center justify-center w-9 h-9 rounded-full transition-all duration-200",
                !message.trim() || isLoading
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              )}
            >
              <img src="/submit.svg" alt="전송" className="w-4 h-4" />
            </button>
          )}
        </div>
        {/* DC-01~03: 면책 고지 영역 */}
        <div className="mt-3 flex flex-col items-center gap-1">
          {isLoggedIn && (
            <p className="text-xs text-gray-500">
              내 최근 의료기록을 반영하고 있어요
            </p>
          )}
          <p className="text-[10px] text-text-tertiary text-center px-4 leading-relaxed">
            AI 답변은 전문 의료인의 답변이 아니므로, 정확한 진단과 치료는 반드시
            의료인과 상담해주세요.
          </p>
        </div>
      </div>

      {/* 문진요약서 모달 */}
      <MedicalSummaryModal
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
        data={summaryData}
        isLoading={isSummaryLoading}
        error={summaryError}
      />

      {/* 건강 위험 & 관리 모달 */}
      <HealthThreatModal
        isOpen={isHealthThreatModalOpen}
        onClose={() => setIsHealthThreatModalOpen(false)}
        data={healthThreatData}
        isLoading={isHealthThreatLoading}
        error={healthThreatError}
      />
    </div>
  );
};

export default HomeScreen;
